import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactFilterRequest } from '#pie/internal/api/pie-bff';
import { useGetContactDetails } from '#pie/internal/api/pie-bff';
import { FormCombobox } from '../form/form-combobox/FormCombobox';
import type { Props as InputComboboxProps } from '../input-combobox/InputCombobox';

interface Props
  extends Omit<
    Partial<ComponentProps<typeof FormCombobox>>,
    'options' | 'name' | 'onLoadMore' | 'totalCount'
  > {
  label: string;
  name?: string;
  filter: ContactFilterRequest;
}

export const FormUserCombobox = ({ name = '', filter, ...props }: Props) => {
  const { t } = useTranslation();

  const [options, setOptions] =
    useState<InputComboboxProps['options']>(undefined);
  const [search, setSearch] = useState<string | undefined>(props.defaultValue);
  const [pageIndex, setPageIndex] = useState(0);

  const { data, isLoading } = useGetContactDetails({
    filter,
    pagination: {
      pageIndex,
      pageSize: 20,
    },
    search,
  });

  useEffect(() => {
    const optionMap = new Map();

    (options || []).forEach((option) => {
      optionMap.set(option.value, option);
    });

    (data?.items || []).forEach((item) => {
      optionMap.set(item.id, { label: item.fullName, value: item.id });
    });

    setOptions(Array.from(optionMap.values()));
  }, [data]);

  const onSearch = (searchTerm: typeof search) => {
    setOptions(undefined);
    setSearch(searchTerm);
    setPageIndex(0);
  };

  return (
    <FormCombobox
      name={name}
      options={options}
      onSearch={onSearch}
      isLoading={isLoading}
      shouldFilter={false}
      placeholder={t('common.filter.search_contact_placeholder')}
      onLoadMore={() => setPageIndex(pageIndex + 1)}
      totalCount={data?.totalCount}
      {...props}
    />
  );
};
